import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination } from "./Pagination";

interface DataTableProps<TData> {
  columns: ColumnDef<TData>[];
  data: TData[];
  total: number;
  limit: number;
  offset: number;
  onPageChange: (offset: number) => void;
  isLoading?: boolean;
  /** When set, the matching row is given a temporary glowing highlight. */
  highlightId?: string | number | null;
  /** When set, double-clicking a row invokes this with the row (e.g. open its view). */
  onRowDoubleClick?: (row: TData) => void;
}

function rowId<TData>(row: TData): unknown {
  const value = (row as { id?: unknown }).id;
  return value;
}

/** Server-paginated grid bound to the API limit/offset contract (README 0.5.3). */
export function DataTable<TData>({
  columns,
  data,
  total,
  limit,
  offset,
  onPageChange,
  isLoading = false,
  highlightId,
  onRowDoubleClick,
}: DataTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-3">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: Math.min(limit, 8) }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {columns.map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => {
                const highlighted =
                  highlightId !== undefined &&
                  highlightId !== null &&
                  String(rowId(row.original)) === String(highlightId);
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={`${highlighted ? "audit-record-highlight" : ""}${
                      onRowDoubleClick ? " cursor-pointer" : ""
                    }`}
                    onDoubleClick={
                      onRowDoubleClick ? () => onRowDoubleClick(row.original) : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Pagination total={total} limit={limit} offset={offset} onPageChange={onPageChange} />
    </div>
  );
}
